import React from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IIrpAddEditRequest } from '../../../../model/scp/irp/request/irp-add-edit-request';
import { Box, Button, Grid, IconButton, InputLabel, LinearProgress, MenuItem, OutlinedInput, Select, Tooltip } from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { IFileResponse } from '../../../../model/scp/base/response/file-response';
import DefaultUploadFile from '../../../../components/default-upload-file';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import PreviewIcon from '@mui/icons-material/Preview';
import { dataGridLocaleConfig } from '../../../../config/data-grid-locale';
import { useIrpFileController } from './controllers/irp-file-controller';

export interface IIrpFileDataTableProps {
    irpRequest?: IIrpAddEditRequest,
    irpId?: string | undefined
};

export default function IrpFileDataTable({ props }: { props: IIrpFileDataTableProps }) {
    const controller = useIrpFileController(props.irpId);

    const {
        fileIrpRequest,
        irpFiles,
        loadMoreData,
        error,
        typeDocument,
        handleOnChange,
        saveFile,
        deleteFile,
        setFileUpload,
        showDialog } = controller;

    const columns: GridColDef<IFileResponse>[] = [
        {
            field: 'number',
            headerName: 'Nº',
            minWidth: 80, sortable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right'
        },
        {
            field: 'title',
            headerName: 'Titulo',
            flex: 1,
            minWidth: 280, sortable: false,
            disableColumnMenu: true
        },
        {
            field: 'documentTypeDescription',
            headerName: 'Tipo',
            flex: 1,
            minWidth: 160, sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            renderCell: (params) => {
                return [
                    <Tooltip title="Visualizar Arquivo" key="preview">
                        <IconButton aria-label="Visualizar" onClick={async () => {
                            window.open(params.row.fileUrl, '_blank');
                        }}>
                            <PreviewIcon />
                        </IconButton>
                    </Tooltip>,
                    <Tooltip title="Excluir" key="delete">
                        <IconButton aria-label="Excluir" onClick={() => {
                            showDialog({
                                title: 'Pergunta', message: 'Deseja realmente excluir o anexo? Caso deseje excluir preencha a justificativa abaixo:', onYesClick: (justification: string) => {
                                    if (params.row.number !== undefined) {
                                        deleteFile(params.row.number, justification);
                                    }
                                }, inputProps: {
                                    rows: 4
                                }
                            } as DefaultDialogProps);
                        }}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                ];
            }
        }
    ];

    return (
        <React.Fragment>
            <Box sx={{ textAlign: 'right' }}>
                <Grid container spacing={2}
                    sx={{ mt: '10px', mb: '10px', pr: '10px', textAlign: 'left' }} >
                    <Grid item xs={12} lg={2}
                        sm={2}
                        xl={2}
                    >
                        <InputLabel>Tipo de Arquivo</InputLabel>
                        <Select
                            onChange={handleOnChange}
                            onBlur={handleOnChange}
                            fullWidth
                            error={fileIrpRequest.documentTypeId === undefined && error ? true : false}
                            value={fileIrpRequest.documentTypeId ? fileIrpRequest.documentTypeId : ''}
                            name='select-document-type-atachment'
                        >
                            <MenuItem value={undefined}>
                                <em>Selecionar</em>
                            </MenuItem>
                            {typeDocument.map((element) => {
                                return (
                                    <MenuItem value={element.id} key={element.id}>{element.name}</MenuItem>
                                );
                            })}
                        </Select>
                    </Grid>
                    <Grid
                        item
                        lg={4}
                        sm={4}
                        xl={4}
                    >
                        <InputLabel>Titulo do Documento</InputLabel>
                        <OutlinedInput
                            name="input-title-atachment"
                            error={(fileIrpRequest.title === undefined || fileIrpRequest.title.length === 0) && error ? true : false}
                            value={fileIrpRequest.title ? fileIrpRequest.title : ''}
                            fullWidth
                            onChange={handleOnChange}
                            onBlur={handleOnChange}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}
                        sm={4}
                        xl={4}
                    >
                        <InputLabel>Tipo de Documento (pdf). Tamanho Max: 30mb </InputLabel>
                        <DefaultUploadFile props={{
                            id: "file-upload-atachment-irp",
                            setFileUpload: setFileUpload,
                            file: fileIrpRequest.document, extensions: ["pdf"], maxFileSizeMb: 30, error: (fileIrpRequest.document === undefined || fileIrpRequest.document === null) && error ? true : false
                        }}></DefaultUploadFile>
                    </Grid>

                    <Grid item xs={12} lg={2}
                        sm={2}
                        xl={2}>
                        <InputLabel> &nbsp; </InputLabel>
                        <Button variant="contained" fullWidth component="label" size="large" sx={{ height: '55px' }} startIcon={<PlaylistAddIcon />}
                            onClick={() => {
                                if (props.irpId !== undefined && props.irpId !== null) {
                                    saveFile();
                                }
                            }}
                        >
                            Adicionar
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <DataGrid
                sx={{ width: '100%', minWidth: 0 }}
                columns={columns}
                disableRowSelectionOnClick
                components={{
                    LoadingOverlay: LinearProgress,
                }}
                loading={loadMoreData}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    pagination: {
                        paginationModel: { pageSize: 25, page: 0 }
                    },
                }}
                rows={irpFiles}
                getRowId={(row: any) => row.number}
                localeText={dataGridLocaleConfig}
            />
        </React.Fragment>
    );
}
